import React, { useEffect, useState } from 'react';
import { Table as AntTable, Space, Popconfirm, Input, Button, Tag } from 'antd';
import { useRef } from "react";
import { SearchOutlined } from '@ant-design/icons';
import { useStateContext } from '../../../../contexts/ContextProvider';
import { UseFetchListEnum } from '../../../../hooks/useFetchEnums';
import { ENUM_TYPE } from '../../../../data/constant';
import dayjs from 'dayjs';

const headerStyle = {
  color: '#525252',
};

// Group mapping
const GROUP_MAPPING = {
  0: { label: 'ไม่เข้า', color: 'red' },
  1: { label: 'เข้า', color: 'green' },
};

const Table = ({ data, onEdit, onDelete, loading, pagination, onPaginationChange }) => {
  const { currentColor } = useStateContext();
  const { mutateAsync: listEnum } = UseFetchListEnum();
  const [rankMapping, setRankMapping] = useState({});
  const [setSearchText] = useState('');
  const [setSearchedColumn] = useState('');
  const searchInput = useRef(null);

  useEffect(() => {
    getEnums();
  }, []);

  const getEnums = async () => {
    try {
      const result = await listEnum({
        enumType: [ENUM_TYPE.vip_rank],
      });

      // Create mapping { value: label }
      const mapping = {};
      result
        .filter((item) => item.type === ENUM_TYPE.vip_rank)
        .forEach((item) => {
          mapping[item.value] = item.label;
        });
      setRankMapping(mapping);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>

          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns = [
    {
      title: 'ลำดับ',
      align: 'center',
      width: 60,
      key: 'number',
      onHeaderCell: () => { return { style: headerStyle } },
      render: (_, __, index) => {
        const currentPage = pagination?.current || 1;
        const pageSize = pagination?.pageSize || 10;
        return (currentPage - 1) * pageSize + index + 1;
      }
    },
    {
      title: 'User ID',
      align: 'center',
      dataIndex: 'userId',
      key: 'userId',
      onHeaderCell: () => { return { style: headerStyle } },
      ...getColumnSearchProps('userId'),
    },
    {
      title: 'Prefix',
      align: 'center',
      dataIndex: 'prefix',
      key: 'prefix',
      onHeaderCell: () => { return { style: headerStyle } },
      render: (text) => text || '-'
    },
    {
      title: 'ชื่อ-นามสกุล',
      align: 'center',
      dataIndex: 'name',
      key: 'name',
      onHeaderCell: () => { return { style: headerStyle } },
      ...getColumnSearchProps('name'),
      render: (text) => text || '-'
    },
    {
      title: 'ชื่อเล่น',
      align: 'center',
      dataIndex: 'nickName',
      key: 'nickName',
      onHeaderCell: () => { return { style: headerStyle } },
      render: (text) => text || '-'
    },
    {
      title: 'Rank',
      align: 'center',
      dataIndex: 'rank',
      key: 'rank',
      width: 100,
      onHeaderCell: () => { return { style: headerStyle } },
      render: (rank) => {
        if (rank === null || rank === undefined) return '-';
        const label = rankMapping[rank] || `Rank ${rank}`;
        return <Tag color="blue">{label}</Tag>;
      }
    },
    {
      title: 'สถานะ',
      align: 'center',
      dataIndex: 'group',
      key: 'group',
      width: 100,
      onHeaderCell: () => { return { style: headerStyle } },
      render: (group) => {
        if (group === null || group === undefined) return '-';
        const info = GROUP_MAPPING[group] || { label: '-', color: 'default' };
        return <Tag color={info.color}>{info.label}</Tag>;
      }
    },
    {
      title: 'วันที่เริ่มต้น',
      align: 'center',
      dataIndex: 'startDate',
      key: 'startDate',
      onHeaderCell: () => { return { style: headerStyle } },
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'การจัดการ',
      fixed: 'right',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (record) => (
        <Space size="middle">
          <div className="text-blue-500 cursor-pointer" onClick={() => onEdit(record)}>
            แก้ไข
          </div>
          <Popconfirm
            title="ลบข้อมูล"
            description="ยืนยันการลบข้อมูล VIP ?"
            onConfirm={() => onDelete(record)}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
            okButtonProps={{ style: { backgroundColor: currentColor, borderColor: currentColor } }}
          >
            <div className="text-red-500 cursor-pointer">ลบ</div>
          </Popconfirm>
        </Space>
      ),
      onHeaderCell: () => ({ style: headerStyle }),
    }
  ];

  return (
    <div className='overflow-x-auto'>
      <AntTable
        loading={loading}
        className='border-1 rounded-lg border-gray-200'
        dataSource={data}
        columns={columns}
        pagination={{
          current: pagination?.current || 1,
          pageSize: pagination?.pageSize || 10,
          total: pagination?.total || 0,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} จาก ${total} รายการ`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, pageSize) => onPaginationChange?.(page, pageSize),
        }}
        rowKey={'id'}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default Table;
